/**
 * WorkstationPerformancePanel — pestaña «Rendimiento» de la ficha del ordenador.
 *
 * Qué se ve: CPU y RAM **ahora** (aguja) y **en el tiempo** (gráfica), más los
 * discos, el tráfico de red por interfaz y los servicios. El rango se elige con
 * los chips de arriba (1 h a 7 días) y los datos se refrescan solos: por
 * WebSocket cuando llega una muestra nueva y cada 30 s como red de seguridad.
 *
 * El agente manda una muestra por ciclo a `POST /api/agent/monitoring/<id>`; el
 * histórico vive 30 días en `workstation_metrics`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { ChartBarSquareIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import HelpDot from '../academy/HelpDot';
import api from '../services/api';
import { useSocket } from '../contexts/SocketContext';
import './WorkstationPerformance.css';

const RANGOS = [
  { horas: 1, label: '1 h' },
  { horas: 6, label: '6 h' },
  { horas: 24, label: '24 h' },
  { horas: 72, label: '3 días' },
  { horas: 168, label: '7 días' },
];

function colorUso(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return 'var(--text-muted)';
  if (n >= 90) return 'var(--red-600, #dc2626)';
  if (n >= 75) return 'var(--amber-600, #d97706)';
  return 'var(--green-600, #16a34a)';
}

function fmtUptime(segundos) {
  if (!segundos && segundos !== 0) return '—';
  const d = Math.floor(segundos / 86400);
  const h = Math.floor((segundos % 86400) / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtBytes(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return '—';
  const v = Number(bytes);
  if (v >= 1099511627776) return `${(v / 1099511627776).toFixed(1)} TB`;
  if (v >= 1073741824) return `${(v / 1073741824).toFixed(1)} GB`;
  if (v >= 1048576) return `${(v / 1048576).toFixed(1)} MB`;
  if (v >= 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${Math.round(v)} B`;
}

function fmtVelocidad(bytesSeg) {
  if (bytesSeg == null || Number.isNaN(Number(bytesSeg))) return '—';
  return `${fmtBytes(bytesSeg)}/s`;
}

function hace(fecha) {
  if (!fecha) return '';
  const s = Math.max(0, Math.round((Date.now() - fecha.getTime()) / 1000));
  if (s < 10) return 'ahora';
  if (s < 60) return `hace ${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `hace ${m} min`;
  return `hace ${Math.round(m / 60)} h`;
}

/** Aguja de 180°. */
function Aguja({ valor, etiqueta, sub }) {
  const size = 132;
  const r = size / 2 - 12;
  const arco = d3.arc().innerRadius(r - 13).outerRadius(r).startAngle(-Math.PI * 0.75);
  const pct = Math.min(Math.max(Number(valor) || 0, 0), 100) / 100;
  const fondo = arco({ endAngle: Math.PI * 0.75 });
  const relleno = arco({ endAngle: -Math.PI * 0.75 + pct * Math.PI * 1.5 });

  return (
    <div className="wperf-gauge">
      <svg width={size} height={size * 0.76} viewBox={`${-size / 2} ${-size / 2} ${size} ${size * 0.76}`}>
        <path d={fondo} fill="var(--slate-100)" />
        {relleno && <path d={relleno} fill={colorUso(valor)} />}
        <text textAnchor="middle" dy="0.1em" fontSize="1.35rem" fontWeight="700" fill="var(--text-primary)">
          {valor == null ? '—' : `${Math.round(Number(valor))}%`}
        </text>
      </svg>
      <div className="wperf-gauge-label">{etiqueta}</div>
      {sub && <div className="wperf-gauge-sub">{sub}</div>}
    </div>
  );
}

/** Gráfica de área + línea sobre el tiempo. */
function Grafica({ datos, campo, etiqueta, color, formatoEje }) {
  const ref = useRef();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    d3.select(el).selectAll('*').remove();
    if (!datos?.length) return;

    const w = el.clientWidth || 420;
    const h = 180;
    const margen = { top: 8, right: 10, bottom: 22, left: 38 };
    const svg = d3.select(el).append('svg').attr('width', w).attr('height', h);

    const fechas = datos.map((d) => new Date(d.timestamp));
    const x = d3.scaleTime().domain(d3.extent(fechas)).range([margen.left, w - margen.right]);
    const maxY = d3.max(datos, (d) => Number(d[campo]) || 0) || 100;
    const y = d3.scaleLinear().domain([0, maxY * 1.1]).nice().range([h - margen.bottom, margen.top]);

    const area = d3.area()
      .x((d) => x(new Date(d.timestamp)))
      .y0(y(0))
      .y1((d) => y(Number(d[campo]) || 0))
      .curve(d3.curveMonotoneX);

    const linea = d3.line()
      .x((d) => x(new Date(d.timestamp)))
      .y((d) => y(Number(d[campo]) || 0))
      .curve(d3.curveMonotoneX);

    svg.append('path').datum(datos).attr('fill', color).attr('opacity', 0.16).attr('d', area);
    svg.append('path').datum(datos).attr('fill', 'none').attr('stroke', color)
      .attr('stroke-width', 1.8).attr('d', linea);

    svg.append('g')
      .attr('transform', `translate(0,${h - margen.bottom})`)
      .call(d3.axisBottom(x).ticks(6).tickFormat(d3.timeFormat('%H:%M')))
      .selectAll('text').attr('fill', 'var(--text-muted)').style('font-size', '0.65rem');

    svg.append('g')
      .attr('transform', `translate(${margen.left},0)`)
      .call(d3.axisLeft(y).ticks(4).tickFormat((v) => (formatoEje ? formatoEje(v) : v)))
      .selectAll('text').attr('fill', 'var(--text-muted)').style('font-size', '0.65rem');

    svg.selectAll('.domain,.tick line').attr('stroke', 'var(--border)');
  }, [datos, campo, color, formatoEje]);

  return (
    <div className="wperf-chart">
      <div className="wperf-chart-label">{etiqueta}</div>
      <div ref={ref} className="wperf-chart-box" />
    </div>
  );
}

export default function WorkstationPerformancePanel({
  companyId,
  workstationId,
  hasAgent = false,
}) {
  const socket = useSocket();
  const [horas, setHoras] = useState(24);
  const [puntos, setPuntos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [, tick] = useState(0);

  const cargar = useCallback(async ({ silencioso = false } = {}) => {
    if (!companyId || !workstationId) return;
    if (!silencioso) setCargando(true);
    try {
      const res = await api.getWorkstationMetrics(workstationId, companyId, horas);
      setPuntos(Array.isArray(res?.data) ? res.data : []);
      setError('');
    } catch (e) {
      if (!silencioso) setError(e.message || 'No se pudieron leer las métricas');
    } finally {
      if (!silencioso) setCargando(false);
    }
  }, [companyId, workstationId, horas]);

  useEffect(() => { cargar(); }, [cargar]);

  // Refresco periódico: el socket puede perderse y el dato no debe quedarse viejo.
  useEffect(() => {
    const id = window.setInterval(() => cargar({ silencioso: true }), 30000);
    return () => window.clearInterval(id);
  }, [cargar]);

  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 5000);
    return () => window.clearInterval(id);
  }, []);

  // Muestra nueva por WebSocket, solo si es de ESTA estación.
  useEffect(() => {
    if (!socket || !workstationId) return undefined;
    const onData = (payload) => {
      if ((payload?.ci_type || '') !== 'workstation') return;
      const id = payload?.workstation_id ?? payload?.server_id ?? payload?.id;
      if (Number(id) !== Number(workstationId)) return;
      setPuntos((prev) => {
        const nuevo = { ...payload };
        const yaEsta = prev.some((p) => nuevo.id && p.id === nuevo.id);
        if (yaEsta) return prev;
        return [...prev, nuevo].slice(-800);
      });
    };
    socket.on('monitoring:data', onData);
    return () => socket.off('monitoring:data', onData);
  }, [socket, workstationId]);

  const ultimo = puntos.length ? puntos[puntos.length - 1] : null;
  const ultimaFecha = ultimo?.timestamp ? new Date(ultimo.timestamp) : null;

  const volumenes = useMemo(() => {
    const lista = Array.isArray(ultimo?.logical_volumes) ? ultimo.logical_volumes : [];
    return lista
      .filter((v) => Number(v.total_gb) > 0)
      .sort((a, b) => Number(b.used_percent || 0) - Number(a.used_percent || 0));
  }, [ultimo]);

  const nics = useMemo(() => {
    const lista = Array.isArray(ultimo?.nic_traffic) ? ultimo.nic_traffic : [];
    return lista
      .slice()
      .sort((a, b) => (Number(b.bytes_recv_sec || 0) + Number(b.bytes_sent_sec || 0))
        - (Number(a.bytes_recv_sec || 0) + Number(a.bytes_sent_sec || 0)));
  }, [ultimo]);

  const servicios = Array.isArray(ultimo?.services) ? ultimo.services : [];
  const serviciosCorriendo = servicios.filter((s) => /running|activo/i.test(String(s.status || ''))).length;
  const serviciosParados = servicios.length - serviciosCorriendo;

  if (!hasAgent) {
    return (
      <div className="wperf-empty">
        <ChartBarSquareIcon />
        <p>
          Este equipo no tiene agente, así que no hay datos de rendimiento.
          Instálalo y en un minuto empezarán a llegar las muestras.
        </p>
      </div>
    );
  }

  return (
    <div className="wperf">
      <div className="wperf-head">
        <div className="wperf-ranges">
          {RANGOS.map((r) => (
            <button
              key={r.horas}
              type="button"
              className={`wperf-chip ${horas === r.horas ? 'is-on' : ''}`}
              onClick={() => setHoras(r.horas)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="wperf-meta">
          {ultimaFecha ? `última muestra ${hace(ultimaFecha)}` : 'sin muestras todavía'}
          <button
            type="button"
            className="wperf-icon"
            onClick={() => cargar()}
            title="Volver a pedir las métricas"
          >
            <ArrowPathIcon />
          </button>
          <HelpDot topic="listados.workstation_performance" label="Ayuda: el rendimiento del equipo" />
        </div>
      </div>

      {error && <div className="wperf-alert">{error}</div>}

      {cargando && !puntos.length && <div className="list-hint">Cargando métricas…</div>}

      {!cargando && !puntos.length && (
        <div className="wperf-empty">
          <ChartBarSquareIcon />
          <p>
            El agente está instalado pero todavía no hay muestras en este rango.
            Envía una cada minuto; si acabas de instalarlo, espera un momento.
          </p>
        </div>
      )}

      {puntos.length > 0 && (
        <>
          <div className="sf2-grid sf2-grid--2">
            <div className="sf2-card">
              <h3>Ahora mismo</h3>
              <div className="wperf-gauges">
                <Aguja valor={ultimo?.cpu_percent} etiqueta="CPU" sub={ultimo?.cpu_count ? `${ultimo.cpu_count} núcleos` : ''} />
                <Aguja
                  valor={ultimo?.ram_percent}
                  etiqueta="RAM"
                  sub={ultimo?.ram_used_gb != null && ultimo?.ram_total_gb != null
                    ? `${Number(ultimo.ram_used_gb).toFixed(1)} / ${Number(ultimo.ram_total_gb).toFixed(0)} GB`
                    : ''}
                />
                <div className="wperf-stat">
                  <div className="wperf-stat-value">{fmtUptime(ultimo?.uptime_seconds)}</div>
                  <div className="wperf-stat-label">Encendido desde</div>
                </div>
              </div>
            </div>

            <div className="sf2-card">
              <h3>Resumen del periodo</h3>
              <div className="sf2-fields" style={{ '--n': 2 }}>
                <div className="sf2-field">
                  <label>CPU media</label>
                  <div className="v">
                    {puntos.length
                      ? `${Math.round(d3.mean(puntos, (p) => Number(p.cpu_percent) || 0))}%`
                      : '—'}
                  </div>
                </div>
                <div className="sf2-field">
                  <label>CPU máxima</label>
                  <div className="v">
                    {puntos.length
                      ? `${Math.round(d3.max(puntos, (p) => Number(p.cpu_percent) || 0))}%`
                      : '—'}
                  </div>
                </div>
                <div className="sf2-field">
                  <label>RAM media</label>
                  <div className="v">
                    {puntos.length
                      ? `${Math.round(d3.mean(puntos, (p) => Number(p.ram_percent) || 0))}%`
                      : '—'}
                  </div>
                </div>
                <div className="sf2-field">
                  <label>Muestras</label>
                  <div className="v">{puntos.length}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="sf2-grid sf2-grid--2">
            <div className="sf2-card">
              <h3>CPU en el tiempo</h3>
              <Grafica datos={puntos} campo="cpu_percent" etiqueta="%" color="#2563eb" formatoEje={(v) => `${v}%`} />
            </div>
            <div className="sf2-card">
              <h3>Memoria en el tiempo</h3>
              <Grafica datos={puntos} campo="ram_percent" etiqueta="%" color="#7c3aed" formatoEje={(v) => `${v}%`} />
            </div>
          </div>

          {volumenes.length > 0 && (
            <div className="sf2-card">
              <h3>Discos</h3>
              <div className="wperf-vols">
                {volumenes.map((vol) => {
                  const pct = Math.min(100, Math.max(0, Number(vol.used_percent) || 0));
                  return (
                    <div key={vol.mount_point || vol.name} className="wperf-vol">
                      <div className="wperf-vol-head">
                        <b>{vol.mount_point || vol.name}</b>
                        <span className="wperf-vol-pct" style={{ color: colorUso(pct) }}>{Math.round(pct)}%</span>
                      </div>
                      <div className="wperf-bar">
                        <div
                          className="wperf-bar-fill"
                          style={{ width: `${pct}%`, background: colorUso(pct) }}
                        />
                      </div>
                      <div className="wperf-vol-sub">
                        {vol.free_gb != null ? `${Number(vol.free_gb).toFixed(1)} GB libres` : '—'}
                        {vol.total_gb != null ? ` de ${Number(vol.total_gb).toFixed(0)} GB` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {nics.length > 0 && (
            <div className="sf2-card">
              <h3>Red</h3>
              <div className="wperf-table">
                <div className="wperf-tr wperf-tr--head">
                  <span>Interfaz</span>
                  <span>Recibido</span>
                  <span>Enviado</span>
                  <span>Total</span>
                  <span>Errores</span>
                </div>
                {nics.map((nic) => (
                  <div key={nic.interface} className="wperf-tr">
                    <span className="wperf-nic">{nic.interface}</span>
                    <span>{fmtVelocidad(nic.bytes_recv_sec)}</span>
                    <span>{fmtVelocidad(nic.bytes_sent_sec)}</span>
                    <span>
                      ↓ {fmtBytes(nic.bytes_recv)} · ↑ {fmtBytes(nic.bytes_sent)}
                    </span>
                    <span>
                      {(Number(nic.errin) || 0) + (Number(nic.errout) || 0)
                        + (Number(nic.dropin) || 0) + (Number(nic.dropout) || 0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {servicios.length > 0 && (
            <div className="sf2-card">
              <h3>
                Servicios
                <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>
                  {serviciosCorriendo} en marcha
                  {serviciosParados > 0 ? ` · ${serviciosParados} parados` : ''}
                </span>
              </h3>
              <div className="wperf-services">
                {servicios.slice(0, 40).map((s) => {
                  const activo = /running|activo/i.test(String(s.status || ''));
                  return (
                    <div key={`${s.name}-${s.display_name || ''}`} className="wperf-service">
                      <span className={`wperf-dot ${activo ? 'is-on' : 'is-off'}`} />
                      <span className="wperf-service-name" title={s.display_name || s.name}>
                        {s.display_name || s.name}
                      </span>
                      <span className="wperf-service-status">{s.status || '—'}</span>
                    </div>
                  );
                })}
                {servicios.length > 40 && (
                  <div className="list-hint">y {servicios.length - 40} más…</div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
